import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import { adminCreateFaq, adminDeleteFaq, adminFaqInsights, adminListFaq, adminTestFaq, adminUpdateFaq, type FaqEntry, type FaqInsights } from '../../../api/faqApi';
import { S, fmtWhen } from './adminPageStyles';

/** FAQ assistant knowledge base (SRS #19): entries, what the bot could not answer, dry-run matching. */

type Draft = { id?: number; question: string; answer: string; category: string; aliases: string; keywords: string; link: string; audience: 'all' | 'user' | 'admin'; published: boolean };
const EMPTY: Draft = { question: '', answer: '', category: 'General', aliases: '', keywords: '', link: '', audience: 'all', published: true };
const split = (s: string) => s.split(/[,\n]/).map(x => x.trim()).filter(Boolean);

const AdminFaqPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const [entries, setEntries] = useState<FaqEntry[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [insights, setInsights] = useState<FaqInsights | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [testQ, setTestQ] = useState('');
  const [testOut, setTestOut] = useState<{ matches: { id: number; question: string; score: number; would: string }[]; thresholds: { answer: number; suggest: number } } | null>(null);

  const load = useCallback(() => {
    adminListFaq().then(r => { setEntries(r.entries); setCategories(r.categories); }).catch(e => setError(e instanceof Error ? e.message : 'Failed to load'));
    adminFaqInsights(30).then(setInsights).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const edit = (e: FaqEntry) => setDraft({ id: e.id, question: e.question, answer: e.answer, category: e.category, aliases: e.aliases.join('\n'), keywords: e.keywords.join(', '), link: e.link || '', audience: e.audience, published: e.published });
  const save = async () => {
    if (!draft) return;
    setError(null); setOk(null);
    const payload = { question: draft.question, answer: draft.answer, category: draft.category, aliases: split(draft.aliases), keywords: split(draft.keywords), link: draft.link || null, audience: draft.audience, published: draft.published };
    try {
      if (draft.id) await adminUpdateFaq(draft.id, payload as Partial<FaqEntry>); else await adminCreateFaq(payload as Partial<FaqEntry>);
      setDraft(null); setOk('Saved.'); load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to save'); }
  };
  const remove = async (e: FaqEntry) => {
    if (!window.confirm(`Delete "${e.question}"?`)) return;
    try { await adminDeleteFaq(e.id); load(); } catch (err) { setError(err instanceof Error ? err.message : 'Failed'); }
  };
  const runTest = async () => { try { setTestOut(await adminTestFaq(testQ)); } catch (e) { setError(e instanceof Error ? e.message : 'Failed'); } };

  const shown = entries.filter(e => !filter || e.question.toLowerCase().includes(filter.toLowerCase()) || e.category.toLowerCase().includes(filter.toLowerCase()));
  const tile = (l: string, v: React.ReactNode) => (
    <div key={l} style={{ border: '1px solid var(--ds-border)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ds-text)' }}>{v}</div>
      <div style={{ fontSize: 12, color: 'var(--ds-text-muted)' }}>{l}</div>
    </div>
  );

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="FAQ assistant" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>FAQ assistant</h2>
        <p style={S.sub}>The support assistant answers only from this knowledge base. Questions it could not answer (or answers people marked unhelpful) appear below — turn them into entries. A question that matches nothing offers the customer a support ticket.</p>
        {error && <div style={S.error}>{error}</div>}
        {ok && <div style={S.ok}>{ok}</div>}

        {insights && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 16 }}>
            {tile('Questions (30 d)', insights.questions)}
            {tile('Answered', insights.answer_rate === null ? '—' : `${Math.round(insights.answer_rate * 100)}%`)}
            {tile('Marked helpful', insights.helpful_rate === null ? '—' : `${Math.round(insights.helpful_rate * 100)}%`)}
            {tile('Escalated to tickets', insights.escalated)}
            {tile('Entries', entries.length)}
          </div>
        )}

        {insights && insights.unanswered.length > 0 && (
          <div style={{ border: '1px solid #fedf89', background: '#fffcf5', borderRadius: 10, padding: 12, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Could not answer</div>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Question</th><th style={S.th}>Asked</th><th style={S.th}>Last</th><th style={S.th}></th></tr></thead>
              <tbody>
                {insights.unanswered.map(u => (
                  <tr key={u.question}>
                    <td style={S.td}>{u.question}</td>
                    <td style={S.td}>{u.count}×</td>
                    <td style={S.td}>{u.last_asked ? fmtWhen(u.last_asked) : '—'}</td>
                    <td style={S.td}><button style={S.btn} onClick={() => setDraft({ ...EMPTY, question: u.question })}>Add as entry</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={S.toolbar}>
          <input style={S.input} placeholder="Filter entries" value={filter} onChange={e => setFilter(e.target.value)} />
          <button style={S.primary} onClick={() => setDraft(EMPTY)}>New entry</button>
          <span style={{ flex: 1 }} />
          <input style={S.input} placeholder="Test a question…" aria-label="Test a question" value={testQ} onChange={e => setTestQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') runTest(); }} />
          <button style={S.btn} onClick={runTest}>Test</button>
        </div>
        {testOut && (
          <div style={{ fontSize: 12.5, marginBottom: 12, color: 'var(--ds-text-secondary)' }}>
            {testOut.matches.length === 0 ? 'No matches.' : testOut.matches.map(m => <div key={m.id}><span style={S.chip}>{m.would}</span> {m.score.toFixed(2)} — {m.question}</div>)}
            <div style={S.muted}>answers at ≥ {testOut.thresholds.answer}, suggests at ≥ {testOut.thresholds.suggest}</div>
          </div>
        )}

        {draft && (
          <div style={{ border: '1px solid var(--ds-border)', borderRadius: 10, padding: 14, marginBottom: 16, display: 'grid', gap: 10 }}>
            <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Question <input aria-label="Question" style={S.input} value={draft.question} onChange={e => setDraft({ ...draft, question: e.target.value })} /></label>
            <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Answer <textarea aria-label="Answer" style={{ ...S.input, minHeight: 90, fontFamily: 'inherit' }} value={draft.answer} onChange={e => setDraft({ ...draft, answer: e.target.value })} /></label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <label style={{ fontSize: 12.5 }}>Category <select aria-label="Category" style={S.input} value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })}>{categories.map(c => <option key={c}>{c}</option>)}</select></label>
              <label style={{ fontSize: 12.5 }}>Audience <select aria-label="Audience" style={{ ...S.input, minWidth: 100 }} value={draft.audience} onChange={e => setDraft({ ...draft, audience: e.target.value as Draft['audience'] })}><option value="all">everyone</option><option value="user">users</option><option value="admin">admins</option></select></label>
              <label style={{ fontSize: 12.5 }}>Link <input aria-label="Link" style={S.input} placeholder="/alerts or https://…" value={draft.link} onChange={e => setDraft({ ...draft, link: e.target.value })} /></label>
              <label style={{ fontSize: 12.5 }}><input type="checkbox" checked={draft.published} onChange={e => setDraft({ ...draft, published: e.target.checked })} /> Published</label>
            </div>
            <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Other ways people ask it (one per line) <textarea aria-label="Aliases" style={{ ...S.input, minHeight: 50, fontFamily: 'inherit' }} value={draft.aliases} onChange={e => setDraft({ ...draft, aliases: e.target.value })} /></label>
            <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Keywords (comma-separated) <input aria-label="Keywords" style={S.input} value={draft.keywords} onChange={e => setDraft({ ...draft, keywords: e.target.value })} /></label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={S.primary} onClick={save}>{draft.id ? 'Save changes' : 'Add entry'}</button>
              <button style={S.btn} onClick={() => setDraft(null)}>Cancel</button>
            </div>
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Question</th><th style={S.th}>Category</th><th style={S.th}>Audience</th><th style={S.th}>Views</th><th style={S.th}>👍 / 👎</th><th style={S.th}>Status</th><th style={S.th}></th></tr></thead>
            <tbody>
              {shown.map(e => (
                <tr key={e.id}>
                  <td style={S.td}>{e.question}<div style={{ ...S.muted, fontSize: 12, maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.answer}</div></td>
                  <td style={S.td}>{e.category}</td>
                  <td style={S.td}>{e.audience}</td>
                  <td style={S.td}>{e.views}</td>
                  <td style={S.td}>{e.helpful} / {e.unhelpful}</td>
                  <td style={S.td}><span style={{ ...S.chip, background: e.published ? 'var(--ds-success-bg)' : 'var(--ds-surface-2)', color: e.published ? 'var(--ds-success)' : 'var(--ds-text-muted)' }}>{e.published ? 'published' : 'draft'}</span></td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                    <button style={S.btn} onClick={() => edit(e)}>Edit</button>{' '}
                    <button style={S.danger} onClick={() => remove(e)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </DashboardLayout>
  );
};

export default AdminFaqPage;
